import { Modal, Pressable, ScrollView, View, Platform, Dimensions, KeyboardAvoidingView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { SubHeading, Caption, Body } from '@/components/ui/Text';

type ImportSheetProps = {
  visible: boolean;
  onClose: () => void;
  tripName?: string;
  canEdit?: boolean;

  onImportBookings?: () => void;
  onImportDocuments?: () => void;
};

export function ImportSheet({
  visible,
  onClose,
  tripName,
  canEdit = true,
  onImportBookings,
  onImportDocuments,
}: ImportSheetProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { height: screenHeight } = Dimensions.get('window');
  const sheetMaxHeight =
    Platform.OS === 'ios'
      ? Math.max(Math.min(screenHeight - insets.top - 60, screenHeight * 0.95), screenHeight * 0.6)
      : Math.min(screenHeight * 0.92, screenHeight - insets.top - 24);
  const sheetMinHeight = Math.min(sheetMaxHeight, Math.max(screenHeight * 0.58, 420));

  const handle =
    (action?: () => void) => () => {
      if (!action) return;
      onClose();
      // iOS and web can drop "open another modal" when we close one and open another in the same tick.
      const delayMs = Platform.OS === 'ios' || Platform.OS === 'web' ? 250 : 0;
      setTimeout(() => action(), delayMs);
    };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      presentationStyle="overFullScreen"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={{ flex: 1, backgroundColor: theme.colors.modalOverlay, justifyContent: 'flex-end' }}>
        <Pressable
          style={{ flex: 1 }}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close import menu"
        />

        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <View
            style={{
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
              maxHeight: sheetMaxHeight,
              minHeight: sheetMinHeight,
              paddingBottom: insets.bottom + theme.spacing(1),
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: theme.spacing(3),
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.borderMuted,
              }}
            >
              <View style={{ flex: 1 }}>
                <SubHeading>Import</SubHeading>
                <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                  {tripName ? `Add bookings and documents to ${tripName}` : 'Add bookings and documents to your trip'}
                </Caption>
              </View>
              <Pressable
                onPress={onClose}
                style={({ pressed }) => ({
                  padding: theme.spacing(1),
                  borderRadius: theme.radii.md,
                  opacity: pressed ? 0.7 : 1,
                })}
                accessibilityRole="button"
                accessibilityLabel="Close import menu"
              >
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </Pressable>
            </View>

            {/* Content */}
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{
                padding: theme.spacing(2),
                paddingBottom: theme.spacing(3),
                gap: theme.spacing(1.5),
              }}
              keyboardShouldPersistTaps="handled"
            >
              {onImportBookings && (
                <Button
                  label="Import bookings (Email)"
                  variant="primary"
                  onPress={handle(onImportBookings)}
                  disabled={!canEdit}
                  icon={<Ionicons name="mail-outline" size={18} color={theme.colors.onPrimary} />}
                  accessibilityLabel="Import bookings from email"
                  accessibilityHint="Connect an email provider to import flight and hotel confirmations"
                />
              )}

              {onImportDocuments && (
                <Button
                  label="Import from documents"
                  variant="secondary"
                  onPress={handle(onImportDocuments)}
                  disabled={!canEdit}
                  icon={<Ionicons name="cloud-upload-outline" size={18} color={theme.colors.text} />}
                  accessibilityLabel="Import from documents"
                  accessibilityHint="Upload a travel document to auto-create itinerary items"
                />
              )}

              {!canEdit && (
                <Body color={theme.colors.textSecondary}>
                  You don’t have permission to import or add items to this trip.
                </Body>
              )}
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}


